import { useState, useEffect } from 'react';
import {Dialog, DialogTitle, DialogContent, Typography, TextField, DialogActions, Button} from '@mui/material';
import { DialogProps } from '../../data';
import { InterchangeActivityTypeLabel } from '../../../constants/securspace-constants';
import DateTimePicker from '../../../deprecatedUikit/DateTimePicker/DateTimePicker';
import {DialogContentContainer, DialogInputContainer, InputFieldContainer, InputSection, ValidatedInputFieldContainer} from './styles'
import { AssetTypeAutocomplete } from '../../../dialogs/asset/autocomplete/AssetTypeAutocomplete';
import { AssetSizeAutocomplete } from '../../../dialogs/asset/autocomplete/AssetSizeAutocomplete';
import { validateContainerNumber } from '../../../../util/ContainerValidator';
import moment from 'moment';

const EditInterchangeDialog = (props: DialogProps) => {
    const { open, interchange, onClose, onAction } = props;

    const [assetType, setAssetType] = useState<string>('');
    const [assetSize, setAssetSize] = useState<string>('');

    const [containerNumber, setContainerNumber] = useState<string>('');
    const [chassisNumber, setChassisNumber] = useState<string>('');
    const [chassisLicensePlateNumber, setChassisLicensePlateNumber] = useState<string>('');
    const [truckLicensePlateNumber, setTruckLicensePlateNumber] = useState<string>('');
    const [trailerNumber, setTrailerNumber] = useState<string>('');
    const [scacCode, setScacCode] = useState<string>('');
    const [sealNumber, setSealNumber] = useState<string>('');
    const [driverFirstName, setDriverFirstName] = useState<string>('');
    const [driverLastName, setDriverLastName] = useState<string>('');
    const [driverLicenseNumber, setDriverLicenseNumber] = useState<string>('');
    const [eventDate, setEventDate] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const [containerError, setContainerError] = useState<string>();

    const handleAssetTypeChange = (_: any, value: {id: string}) => {
        if(value) {
            setAssetType(value.id);
        }
    };

    const handleAssetSizeChange = (_: any, value: {id: string, label: string}) => {
        if(value) {
            setAssetSize(value.id);
        }
    };

    const handleContainerFieldChange = (e: {target: {value: string}}) => {
        const {value} = e.target;
        if (value && !validateContainerNumber(value)) {
            setContainerError('Container number is invalid')
        } else {
            setContainerError('')
        }
        setContainerNumber(value)
    };

    const handleTextFieldChange = (e: {target: {value: string}}, onChange: (value: string) => void) => {
        const {value} = e.target;
        onChange(value);
    };

    const handleDateChange = (date: any | null) => {
      if(date) {
        setEventDate(date)
      }
    };

    const handleClose = () => {
        setAssetType("")
        setAssetSize('')
        setContainerNumber("")
        setChassisNumber("")
        setChassisLicensePlateNumber("")
        setTruckLicensePlateNumber("")
        setScacCode("")
        setDriverFirstName("")
        setDriverLastName("")
        setDriverLicenseNumber("")
        setSealNumber("")
        setEventDate('')
        setNotes("")

        onClose();
    }

    const handleSubmit = () => {
        if(interchange) {
            const {id, activity} = interchange;

            const editedInterchange = {
                id: id,
                containerNumber: containerNumber,
                chassisNumber: chassisNumber,
                chassisLicensePlateNumber: chassisLicensePlateNumber,
                sealNumber: sealNumber,
                firstName: driverFirstName,
                lastName: driverLastName,
                trailerNumber: trailerNumber,
                licenseNumber: driverLicenseNumber,
                truckLicensePlate: truckLicensePlateNumber,
                scacCode: scacCode,
                notes: notes,
                assetType: assetType,
                assetSize: assetSize,
                eventDate: moment(eventDate).toISOString(true),
                eventType: activity,
            }
            onAction(editedInterchange);
            onClose();
        }
    };

    useEffect(() => {
        if(interchange) {
            setAssetType(interchange.assetType)
            setAssetSize(interchange.assetSize)
            setContainerNumber(interchange.containerNumber)
            setChassisNumber(interchange.chassisNumber)
            setChassisLicensePlateNumber(interchange.chassisLicensePlateNumber)
            setTruckLicensePlateNumber(interchange.truckLicensePlateNumber)
            setTrailerNumber(interchange.trailerNumber)
            setSealNumber(interchange.sealNumber)
            setScacCode(interchange.scacCode)
            setDriverFirstName(interchange.driverFirstName)
            setDriverLastName(interchange.driverLastName)
            setDriverLicenseNumber(interchange.driverLicenseNumber)
            setEventDate(interchange.activityDate)
            setNotes(interchange.notes)
        }
    }, [interchange]);

    useEffect(() => {
      if ( interchange?.assetType !== assetType ||
        !moment(interchange?.activityDate).isSame(moment(eventDate)) ||
        interchange?.assetSize !== assetSize ||
        interchange?.containerNumber !== containerNumber ||
        interchange?.chassisNumber !== chassisNumber ||
        interchange?.chassisLicensePlateNumber !== chassisLicensePlateNumber ||
        interchange?.truckLicensePlateNumber !== truckLicensePlateNumber ||
        interchange?.trailerNumber !== trailerNumber ||
        interchange?.sealNumber !== sealNumber ||
        interchange?.scacCode !== scacCode ||
        interchange?.driverFirstName !== driverFirstName ||
        interchange?.driverLastName !== driverLastName ||
        interchange?.driverLicenseNumber !== driverLicenseNumber ||
        interchange?.notes !== notes
      ) {
        setDisableSubmit(false)
      } else setDisableSubmit(true)
    }, [
      interchange,
      assetType,
      eventDate,
      assetSize,
      containerNumber,
      chassisNumber,
      chassisLicensePlateNumber,
      truckLicensePlateNumber,
      trailerNumber,
      sealNumber,
      scacCode,
      driverFirstName,
      driverLastName,
      driverLicenseNumber,
      notes
    ]);

    const datePickerLabel = `${InterchangeActivityTypeLabel[interchange?.activity as keyof typeof InterchangeActivityTypeLabel] ?? 'Activity'} Date`;

    return <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle variant={'h6'}>Edit Equipment</DialogTitle>
            <DialogContent>
                {
                    interchange && <DialogContentContainer>
                        <DialogInputContainer>
                            <AssetTypeAutocomplete
                                value={assetType}
                                onChange={handleAssetTypeChange}
                            />
                            <AssetSizeAutocomplete
                                value={assetSize}
                                onChange={handleAssetSizeChange}
                            />
                            <DateTimePicker
                              label={datePickerLabel}
                              value={eventDate}
                              onChange={handleDateChange}
                            />
                        </DialogInputContainer>
                        <DialogInputContainer>
                            <ValidatedInputFieldContainer>
                                <Typography width='70%'>Container Number</Typography>
                                <TextField
                                    name='containerNumber'
                                    onChange={handleContainerFieldChange}
                                    value={containerNumber}
                                    variant='standard'
                                    helperText={containerError}
                                    FormHelperTextProps={{error: true}}
                                    fullWidth
                                />
                            </ValidatedInputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Chassis Number</Typography>
                                <TextField
                                    name='chassisNumber'
                                    onChange={(e) => handleTextFieldChange(e, setChassisNumber)}
                                    value={chassisNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Trailer Number</Typography>
                                <TextField
                                    name='trailerNumber'
                                    onChange={(e) => handleTextFieldChange(e, setTrailerNumber)}
                                    value={trailerNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Truck License Plate Number</Typography>
                                <TextField
                                    name='truckLicensePlateNumber'
                                    onChange={(e) => handleTextFieldChange(e, setTruckLicensePlateNumber)}
                                    value={truckLicensePlateNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Chassis License Plate Number</Typography>
                                <TextField
                                    name='chassisLicensePlateNumber'
                                    onChange={(e) => handleTextFieldChange(e, setChassisLicensePlateNumber)}
                                    value={chassisLicensePlateNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>Seal Number</Typography>
                                <TextField
                                    name='sealNumber'
                                    onChange={(e) => handleTextFieldChange(e, setSealNumber)}
                                    value={sealNumber}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                            <InputFieldContainer>
                                <Typography width='70%'>SCAC Code</Typography>
                                <TextField
                                    name='scacCode'
                                    onChange={(e) => handleTextFieldChange(e, setScacCode)}
                                    value={scacCode}
                                    variant='standard'
                                    fullWidth
                                />
                            </InputFieldContainer>
                        </DialogInputContainer>
                        <DialogInputContainer>
                                <InputSection>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver First Name</Typography>
                                        <TextField
                                            name='driverFirst'
                                            onChange={(e) => handleTextFieldChange(e, setDriverFirstName)}
                                            value={driverFirstName}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver Last Name</Typography>
                                        <TextField
                                            name='driverLast'
                                            onChange={(e) => handleTextFieldChange(e, setDriverLastName)}
                                            value={driverLastName}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                    <InputFieldContainer>
                                        <Typography width='70%'>Driver License Number</Typography>
                                        <TextField
                                            name='driverLicenseNumber'
                                            onChange={(e) => handleTextFieldChange(e, setDriverLicenseNumber)}
                                            value={driverLicenseNumber}
                                            variant='standard'
                                            fullWidth
                                        />
                                    </InputFieldContainer>
                                </InputSection>
                                <InputFieldContainer>
                                    <Typography width='70%'>Notes</Typography>
                                    <TextField
                                        name='notes'
                                        onChange={(e) => handleTextFieldChange(e, setNotes)}
                                        value={notes}
                                        variant='standard'
                                        fullWidth
                                    />
                                </InputFieldContainer>
                        </DialogInputContainer>
                    </DialogContentContainer>
                }
            </DialogContent>
            <DialogActions>
                <Button disabled={disableSubmit} onClick={handleSubmit}>Save Changes</Button>
                <Button onClick={handleClose}>Exit</Button>
            </DialogActions>
        </Dialog>
}

export default EditInterchangeDialog;
